import type { RequestTypeVersionFullDTO, WorkflowStepConfig } from '@kfolks/types'
import { useQuery } from '@tanstack/react-query'
import { Modal } from '@/components/Modal'
import { requestCenterApi } from '@/features/request-center'
import { canonicalStringify } from '../json-canonicalize'
import {
	computeDeepDiff,
	type DeepDiff,
	type FormFieldChange,
	type FormSchemaDiff,
	type PropertyChange,
	type SummaryTemplateDiff,
	type WorkflowStepChange,
} from './deep-diff'

/**
 * Full field-level diff modal (task 9.3 — design Q6 resolved). Opened from
 * a History row's Diff button in `VersionHistoryPanel`. Fetches v(N) and
 * v(N-1) full payloads (React Query caches share with the panel's
 * pre-fetch — usually instant) and renders the structured diff produced
 * by `computeDeepDiff`.
 *
 * v1 row case: `previousVersionId` is null and the modal renders the
 * entire payload as "added" (no left side). The Diff button on a v1 row
 * is disabled today, so this branch isn't user-reachable from the panel,
 * but the modal supports it for symmetry.
 */

export interface VersionDiffModalProps {
	open: boolean
	onClose: () => void
	requestTypeId: string
	versionId: string | null
	previousVersionId: string | null
}

export function VersionDiffModal({
	open,
	onClose,
	requestTypeId,
	versionId,
	previousVersionId,
}: VersionDiffModalProps) {
	const currentQuery = useQuery({
		queryKey: ['request-type-version', requestTypeId, versionId],
		queryFn: () => requestCenterApi.getRequestTypeVersion(requestTypeId, versionId!),
		enabled: open && Boolean(requestTypeId) && Boolean(versionId),
	})
	const previousQuery = useQuery({
		queryKey: ['request-type-version', requestTypeId, previousVersionId],
		queryFn: () => requestCenterApi.getRequestTypeVersion(requestTypeId, previousVersionId!),
		enabled: open && Boolean(requestTypeId) && Boolean(previousVersionId),
	})

	const title = versionId ? (previousVersionId ? `Diff: comparing versions` : `Initial version`) : 'Version diff'

	return (
		<Modal open={open} onClose={onClose} title={title} maxWidth="3xl">
			<DiffBody
				current={currentQuery.data ?? null}
				previous={previousQuery.data ?? null}
				isLoading={(open && currentQuery.isLoading) || (open && Boolean(previousVersionId) && previousQuery.isLoading)}
				isError={currentQuery.isError || (Boolean(previousVersionId) && previousQuery.isError)}
				expectsPrevious={Boolean(previousVersionId)}
			/>
		</Modal>
	)
}

interface DiffBodyProps {
	current: RequestTypeVersionFullDTO | null
	previous: RequestTypeVersionFullDTO | null
	isLoading: boolean
	isError: boolean
	expectsPrevious: boolean
}

function DiffBody({ current, previous, isLoading, isError, expectsPrevious }: DiffBodyProps) {
	if (isLoading) {
		return (
			<div role="status" aria-busy="true" aria-label="Loading diff" className="space-y-2">
				<div className="h-6 animate-pulse rounded-md bg-muted" />
				<div className="h-32 animate-pulse rounded-md bg-muted" />
			</div>
		)
	}
	if (isError) {
		return (
			<div
				role="alert"
				className="rounded-md border border-destructive/40 bg-destructive/10 p-3 text-sm text-destructive"
			>
				Failed to load one or both versions. Please retry.
			</div>
		)
	}
	if (!current) return null
	const diff = computeDeepDiff(current, expectsPrevious ? previous : null)
	return (
		<div className="space-y-4 text-sm" data-testid="version-diff-body">
			<DiffHeader current={current} previous={expectsPrevious ? previous : null} />
			<FormSchemaSection diff={diff.formSchema} />
			<WorkflowConfigSection diff={diff.workflowConfig} />
			<SummaryTemplateSection diff={diff.summaryTemplate} />
		</div>
	)
}

function DiffHeader({
	current,
	previous,
}: {
	current: RequestTypeVersionFullDTO
	previous: RequestTypeVersionFullDTO | null
}) {
	return (
		<p className="text-xs text-muted-foreground">
			{previous ? (
				<>
					Comparing <strong>v{current.versionNumber}</strong> to <strong>v{previous.versionNumber}</strong>
				</>
			) : (
				<>
					Showing <strong>v{current.versionNumber}</strong> (no previous version)
				</>
			)}
		</p>
	)
}

// ─── formSchema section ──────────────────────────────────────────────────────

function FormSchemaSection({ diff }: { diff: DeepDiff['formSchema'] }) {
	const empty = diff.addedFields.length === 0 && diff.removedFields.length === 0 && diff.changedFields.length === 0
	return (
		<DiffSection title="Form fields" empty={empty}>
			<FieldList label="Added" tone="positive" items={diff.addedFields.map((f) => f.id)} testid="diff-form-added" />
			<FieldList
				label="Removed"
				tone="negative"
				items={diff.removedFields.map((f) => f.id)}
				testid="diff-form-removed"
			/>
			<FieldChangeList changes={diff.changedFields} />
		</DiffSection>
	)
}

function FieldChangeList({ changes }: { changes: FormSchemaDiff['changedFields'] }) {
	if (changes.length === 0) return null
	return (
		<div data-testid="diff-form-changed" className="space-y-2">
			<p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Changed</p>
			<ul className="space-y-1">
				{changes.map((c) => (
					<FieldChangeRow key={c.id} change={c} />
				))}
			</ul>
		</div>
	)
}

function FieldChangeRow({ change }: { change: FormFieldChange }) {
	return (
		<li className="rounded border border-amber-300/60 bg-amber-50/50 p-2 dark:bg-amber-950/20">
			<p className="font-mono text-xs font-medium">{change.id}</p>
			<PropertyChangeList properties={change.properties} />
		</li>
	)
}

// ─── workflowConfig section ──────────────────────────────────────────────────

function WorkflowConfigSection({ diff }: { diff: DeepDiff['workflowConfig'] }) {
	const empty =
		diff.addedSteps.length === 0 &&
		diff.removedSteps.length === 0 &&
		diff.changedSteps.length === 0 &&
		diff.reorderedStepIds.length === 0
	return (
		<DiffSection title="Workflow steps" empty={empty}>
			<FieldList label="Added" tone="positive" items={diff.addedSteps.map(stepLabel)} testid="diff-workflow-added" />
			<FieldList
				label="Removed"
				tone="negative"
				items={diff.removedSteps.map(stepLabel)}
				testid="diff-workflow-removed"
			/>
			<StepChangeList changes={diff.changedSteps} />
			{diff.reorderedStepIds.length > 0 ? (
				<p data-testid="diff-workflow-reordered" className="text-xs">
					<span className="font-semibold uppercase tracking-wide text-muted-foreground">Reordered:</span>{' '}
					<span className="font-mono">{diff.reorderedStepIds.join(' → ')}</span>
				</p>
			) : null}
		</DiffSection>
	)
}

function StepChangeList({ changes }: { changes: ReadonlyArray<WorkflowStepChange> }) {
	if (changes.length === 0) return null
	return (
		<div data-testid="diff-workflow-changed" className="space-y-2">
			<p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Changed</p>
			<ul className="space-y-1">
				{changes.map((c) => (
					<li key={c.id} className="rounded border border-amber-300/60 bg-amber-50/50 p-2 dark:bg-amber-950/20">
						<p className="font-mono text-xs font-medium">{c.id}</p>
						<PropertyChangeList properties={c.properties} />
					</li>
				))}
			</ul>
		</div>
	)
}

// ─── summaryTemplate section ─────────────────────────────────────────────────

function SummaryTemplateSection({ diff }: { diff: SummaryTemplateDiff }) {
	if (!diff.changed) {
		return (
			<DiffSection title="Summary template" empty={true}>
				{null}
			</DiffSection>
		)
	}
	return (
		<DiffSection title="Summary template" empty={false}>
			<div className="space-y-1" data-testid="diff-summary-changed">
				<BeforeAfterPair before={diff.before} after={diff.after} />
			</div>
		</DiffSection>
	)
}

// ─── shared building blocks ──────────────────────────────────────────────────

function DiffSection({ title, empty, children }: { title: string; empty: boolean; children: React.ReactNode }) {
	return (
		<section className="rounded-md border bg-card p-3">
			<h4 className="mb-2 text-sm font-semibold">{title}</h4>
			{empty ? (
				<p className="text-xs italic text-muted-foreground">No changes.</p>
			) : (
				<div className="space-y-2">{children}</div>
			)}
		</section>
	)
}

function FieldList({
	label,
	tone,
	items,
	testid,
}: {
	label: string
	tone: 'positive' | 'negative'
	items: ReadonlyArray<string>
	testid: string
}) {
	if (items.length === 0) return null
	const toneClass =
		tone === 'positive'
			? 'border-emerald-300/60 bg-emerald-50/50 text-emerald-700 dark:bg-emerald-950/20 dark:text-emerald-400'
			: 'border-rose-300/60 bg-rose-50/50 text-rose-700 dark:bg-rose-950/20 dark:text-rose-400'
	return (
		<div data-testid={testid} className="space-y-1">
			<p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{label}</p>
			<ul className="flex flex-wrap gap-1">
				{items.map((id) => (
					<li key={id} className={`rounded border px-2 py-0.5 font-mono text-xs ${toneClass}`}>
						{id}
					</li>
				))}
			</ul>
		</div>
	)
}

function PropertyChangeList({ properties }: { properties: ReadonlyArray<PropertyChange> }) {
	return (
		<ul className="mt-1 space-y-1">
			{properties.map((p) => (
				<li key={p.property} className="text-xs">
					<span className="font-medium">{p.property}: </span>
					<BeforeAfterInline before={p.before} after={p.after} />
				</li>
			))}
		</ul>
	)
}

function BeforeAfterInline({ before, after }: { before: unknown; after: unknown }) {
	return (
		<>
			<code className="rounded bg-rose-100 px-1 dark:bg-rose-950/40">{stringifyForDisplay(before)}</code>
			<span className="mx-1 text-muted-foreground">→</span>
			<code className="rounded bg-emerald-100 px-1 dark:bg-emerald-950/40">{stringifyForDisplay(after)}</code>
		</>
	)
}

function BeforeAfterPair({ before, after }: { before: string | null; after: string | null }) {
	return (
		<div className="grid grid-cols-1 gap-2 md:grid-cols-2">
			<pre className="overflow-auto rounded bg-rose-50 p-2 text-xs dark:bg-rose-950/20">
				{before === null ? '(none)' : before}
			</pre>
			<pre className="overflow-auto rounded bg-emerald-50 p-2 text-xs dark:bg-emerald-950/20">
				{after === null ? '(none)' : after}
			</pre>
		</div>
	)
}

// ─── helpers ─────────────────────────────────────────────────────────────────

function stepLabel(step: WorkflowStepConfig): string {
	return step.label ? `${step.id} (${step.label})` : step.id
}

function stringifyForDisplay(value: unknown): string {
	if (value === undefined) return '(absent)'
	if (value === null) return 'null'
	if (typeof value === 'string') return JSON.stringify(value)
	const json = canonicalStringify(value)
	return json.length > 80 ? json.slice(0, 80) + '…' : json
}
